// Cálculos puros para el reporte "Detergentes y Químicos" (sin JSX), reutilizados por
// las secciones de presentación.
//
// Unidades: el consumo TEÓRICO (reception.productosUsados) viene en MILILITROS; el
// consumo REAL (inventoryregistries, tipoMovimiento: 'consumo') viene en LITROS — por eso
// siempre se divide el teórico entre 1000 antes de comparar.
//
// Un producto puede aparecer solo en un lado:
// - teórico > 0, real = 0 -> 'sin_consumo' (la fórmula lo pide, no se ha tomado del inventario)
// - real > 0, teórico = 0 -> 'sin_formula' (se consumió pero ningún programa lo pide)
// - ambos > 0 -> se compara contra la tolerancia (Laundry.indicadores.detergentes.porcentajeAceptacion)

const DEFAULT_TOLERANCIA_PCT = 15;

// Une el consumo teórico (por producto, ml) con el real (por producto, L) y los costos
// teóricos (mapa productoId -> costo por litro).
export function mergeProductos({ productosTotales = [], real = [], costosPorLitro = new Map(), toleranciaPct }) {
  const tolerancia = Number(toleranciaPct) > 0 ? Number(toleranciaPct) : DEFAULT_TOLERANCIA_PCT;
  const map = new Map();

  productosTotales.forEach((p) => {
    const id = p.productoId || 'sin-id';
    map.set(id, {
      id,
      nombre: p.productoNombre || 'Producto',
      teoricoL: Number(p.totalMl || 0) / 1000,
      realL: 0,
      realCosto: 0,
    });
  });

  real.forEach((r) => {
    const id = r?.producto?.id || 'sin-id';
    const existing = map.get(id);
    if (existing) {
      existing.realL = Number(r.totalUsado || 0);
      existing.realCosto = Number(r.totalCosto || 0);
    } else {
      map.set(id, {
        id,
        nombre: r?.producto?.nombre || 'Producto',
        teoricoL: 0,
        realL: Number(r.totalUsado || 0),
        realCosto: Number(r.totalCosto || 0),
      });
    }
  });

  const rows = Array.from(map.values()).map((p) => {
    const costoPorLitroTeorico = costosPorLitro.get(p.id) || 0;
    const teoricoCosto = p.teoricoL * costoPorLitroTeorico;
    const difL = p.realL - p.teoricoL;
    const difPct = p.teoricoL > 0 ? (difL / p.teoricoL) * 100 : null;

    let estado;
    if (p.teoricoL === 0 && p.realL > 0) estado = 'sin_formula';
    else if (p.teoricoL > 0 && p.realL === 0) estado = 'sin_consumo';
    else if (Math.abs(difPct) <= tolerancia) estado = 'en_rango';
    else estado = 'sobre';

    return {
      ...p,
      costoPorLitroTeorico,
      teoricoCosto,
      difL,
      difPct,
      estado,
    };
  });

  // Orden: mayor consumo real primero (coincide con lo que más pesa operativamente)
  rows.sort((a, b) => b.realL - a.realL || b.teoricoL - a.teoricoL);
  return rows;
}

export function computeHeroKpis(merged, kilosProcesados) {
  const kg = Number(kilosProcesados) || 0;
  const litrosRealTotal = merged.reduce((s, p) => s + p.realL, 0);
  const litrosTeoricoTotal = merged.reduce((s, p) => s + p.teoricoL, 0);
  const costoRealTotal = merged.reduce((s, p) => s + p.realCosto, 0);
  const costoTeoricoTotal = merged.reduce((s, p) => s + p.teoricoCosto, 0);

  const mlKgReal = kg > 0 ? (litrosRealTotal * 1000) / kg : 0;
  const mlKgTeorico = kg > 0 ? (litrosTeoricoTotal * 1000) / kg : 0;
  const pctDesviacion = mlKgTeorico > 0 ? ((mlKgReal - mlKgTeorico) / mlKgTeorico) * 100 : 0;

  const costoKgReal = kg > 0 ? costoRealTotal / kg : 0;
  const costoKgTeorico = kg > 0 ? costoTeoricoTotal / kg : 0;
  const sobrecostoKg = costoKgReal - costoKgTeorico;
  const sobrecostoTotal = costoRealTotal - costoTeoricoTotal;
  const costoPromedioLitro = litrosRealTotal > 0 ? costoRealTotal / litrosRealTotal : 0;

  return {
    kilosProcesados: kg,
    litrosRealTotal,
    litrosTeoricoTotal,
    costoRealTotal,
    costoTeoricoTotal,
    mlKgReal,
    mlKgTeorico,
    pctDesviacion,
    costoKgReal,
    costoKgTeorico,
    sobrecostoKg,
    sobrecostoTotal,
    costoPromedioLitro,
  };
}

export function buildAutoObservations(merged, heroKpis) {
  const obs = [];
  const fmtL = (n) => `${Number(n || 0).toLocaleString('es-CO', { maximumFractionDigits: 1 })} L`;
  const fmtCop = (n) => `$ ${Math.round(Number(n || 0)).toLocaleString('es-CO')}`;

  const sinConsumo = merged.filter((p) => p.estado === 'sin_consumo').sort((a, b) => b.teoricoL - a.teoricoL);
  const sinFormula = merged.filter((p) => p.estado === 'sin_formula').sort((a, b) => b.realL - a.realL);
  if (sinConsumo.length > 0 || sinFormula.length > 0) {
    const partes = [];
    if (sinConsumo.length > 0) {
      partes.push(`${sinConsumo[0].nombre} aparece en la fórmula (${fmtL(sinConsumo[0].teoricoL)} teóricos) y su consumo real fue cero`);
    }
    if (sinFormula.length > 0) {
      const totalSinFormulaL = sinFormula.reduce((s, p) => s + p.realL, 0);
      partes.push(`${sinFormula[0].nombre}${sinFormula.length > 1 ? ` y ${sinFormula.length - 1} producto(s) más` : ''} sumaron ${fmtL(totalSinFormulaL)} sin estar en ninguna fórmula`);
    }
    obs.push({
      titulo: 'La fórmula cargada no es la que se usa',
      cuerpo: `${partes.join('. ')}. Antes de leer la desviación como sobre-consumo hay que revisar si las fórmulas de los programas están actualizadas.`,
    });
  }

  const topCosto = [...merged].sort((a, b) => b.realCosto - a.realCosto)[0];
  if (topCosto && heroKpis.costoRealTotal > 0) {
    const pct = (topCosto.realCosto / heroKpis.costoRealTotal) * 100;
    obs.push({
      titulo: 'Un producto explica buena parte del gasto',
      cuerpo: `${topCosto.nombre} concentra ${fmtCop(topCosto.realCosto)} de los ${fmtCop(heroKpis.costoRealTotal)} gastados en el periodo (${pct.toFixed(0)}%). Revisar si está sustituyendo al producto base o si se está sobredosificando.`,
    });
  }

  if (Math.abs(heroKpis.sobrecostoTotal) > 0) {
    const esSobrecosto = heroKpis.sobrecostoTotal > 0;
    obs.push({
      titulo: esSobrecosto ? 'El sobre-consumo tiene un costo' : 'El consumo quedó por debajo del teórico',
      cuerpo: `${fmtCop(Math.abs(heroKpis.sobrecostoTotal))} de diferencia frente al costo teórico del periodo, equivalente a ${fmtCop(Math.abs(heroKpis.sobrecostoKg))} por kilo procesado.`,
    });
  }

  return obs;
}
